import { Input, Select } from '@servicecompressionllc/sc-design-system';
import { obviousPersonField } from '../definitionEdit.js';
import type { StepEditorProps } from './types.js';
import { useBotStepOptions } from './stepOptions.js';

const LABEL = 'grid min-w-0 gap-1 text-xs text-ink-muted';

export const BotStepEditor = ({ step, vars, onChange: put }: StepEditorProps<'bot'>) => {
  const options = useBotStepOptions();
  const r = step.recipients;
  const eventFields = vars.filter((v) => v.source === 'event');

  return (
    <>
      <div className="flex flex-wrap gap-3">
        <label className={`${LABEL} w-60`}>
          <span>
            <span className="font-semibold text-ink">Bot</span> · who sends it
          </span>
          <Select
            aria-label="Bot"
            options={options.botOptions}
            value={step.botId ? [step.botId] : []}
            placeholder={options.botsError ?? 'Pick a bot'}
            emptyMessage="No bots match."
            onChange={(ids) => put({ ...step, botId: ids[0] ?? '' })}
          />
        </label>
        <label className={`${LABEL} w-64`}>
          <span className="font-semibold text-ink">Who gets told</span>
          <Select
            aria-label="Who gets told"
            searchable={false}
            options={[
              { value: 'from_event', label: 'A person named in the event' },
              { value: 'group', label: 'Everyone in a group' },
            ]}
            value={[r.kind]}
            onChange={(v) =>
              put({
                ...step,
                recipients:
                  v[0] === 'group'
                    ? { kind: 'group', groupId: '' }
                    : { kind: 'from_event', field: obviousPersonField(eventFields) },
              })
            }
          />
        </label>
        {r.kind === 'from_event' ? (
          <label className={`${LABEL} w-56`}>
            <span>
              <span className="font-semibold text-ink">Which field</span> · from the event
            </span>
            {eventFields.length > 0 ? (
              <Select
                aria-label="Which field"
                options={eventFields.map((v) => ({ value: v.path, label: v.label }))}
                value={r.field ? [r.field] : []}
                placeholder="Pick a field"
                emptyMessage="No fields match."
                onChange={(v) =>
                  put({ ...step, recipients: { kind: 'from_event', field: v[0] ?? '' } })
                }
              />
            ) : (
              // No registered fields yet (a custom event that hasn't arrived): type the field.
              <Input
                aria-label="Which field"
                placeholder="e.g. payload.mechanicEmail"
                value={r.field}
                onChange={(e) =>
                  put({ ...step, recipients: { kind: 'from_event', field: e.target.value } })
                }
              />
            )}
          </label>
        ) : (
          <label className={`${LABEL} w-56`}>
            <span className="font-semibold text-ink">Which group</span>
            <Select
              aria-label="Which group"
              options={options.groupOptions}
              value={r.groupId ? [r.groupId] : []}
              placeholder={options.groupsError ?? 'Pick a group'}
              emptyMessage="No groups match."
              onChange={(v) => put({ ...step, recipients: { kind: 'group', groupId: v[0] ?? '' } })}
            />
          </label>
        )}
      </div>
      {options.botsError && <p className="text-xs text-danger">{options.botsError}</p>}
      {r.kind === 'group' && options.groupsError && (
        <p className="text-xs text-danger">{options.groupsError}</p>
      )}
      <p className="-mt-1 text-xs text-ink-subtle">
        {r.kind === 'from_event'
          ? 'Each value is matched to a person by email, or by an exact full name. Anyone who doesn’t match shows as a warning on the run, and everyone else still gets told.'
          : 'Everyone in the group who has this bot on gets their own message.'}
      </p>

      <label className={LABEL}>
        <span>
          <span className="font-semibold text-ink">Goal</span> · optional, a particular angle
        </span>
        <textarea
          aria-label="Goal"
          value={step.goal}
          onChange={(e) => put({ ...step, goal: e.target.value })}
          rows={2}
          placeholder="Leave empty and it tells them what happened and what it means for them. Or ask for an angle, like “any trends on why this keeps happening”."
          className="rounded-md border border-outline bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-outline-strong"
        />
      </label>

      <label className="flex items-center gap-2 text-xs font-semibold text-ink-muted">
        <input
          type="checkbox"
          className="accent-primary"
          checked={step.ask ?? false}
          onChange={(e) => put({ ...step, ask: e.target.checked })}
        />
        Ask for a decision
      </label>
      <p className="-mt-1 text-xs text-ink-subtle">
        {step.ask
          ? 'Each message ends in a question for the people told, with buttons when they pick among named people, places or parts. The first answer, from any of them, counts for everyone, and the bot then does what it said it would for whoever answered. Say what to ask in the goal.'
          : 'The bot tells them; nobody needs to answer.'}
      </p>

      <p className="text-xs text-ink-subtle">
        The bot gets the whole event and every earlier step’s answer, works out what matters, and
        writes the headline and message for each person, using what it remembers about them. A bot
        sends at most 6 messages an hour to one person. Run a test to see who would be told; nothing
        is sent.
      </p>
    </>
  );
};
